/**
 * Centralized image path constants and helpers.
 * Single source of truth for all image paths used in OG images, hero banners, icons, and maps.
 *
 * All images are served from GCS (Google Cloud Storage).
 * No local images in public/ — everything goes through the almanak-event-heroes bucket.
 */

import { CALENDAR_YEAR } from './config';
import { SITE_URL } from './brand';

const GCS_BASE = 'https://storage.googleapis.com/almanak-event-heroes';

export { GCS_BASE };

export const DEFAULT_OG_IMAGE = `${GCS_BASE}/landing/og-background.webp`;

/** Resolve an image path to an absolute URL, handling relative paths from DB */
export function toAbsoluteImageUrl(path: string): string {
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  return path.startsWith('/') ? `${SITE_URL}${path}` : `${SITE_URL}/${path}`;
}

// --- OG images (social sharing) ---

export const EVENT_TYPE_OG_IMAGES: Record<string, string> = {
  public_holiday:      `${GCS_BASE}/events/og/public-holiday.webp`,
  national_day:        `${GCS_BASE}/events/og/national-day.webp`,
  cultural_festival:   `${GCS_BASE}/events/og/cultural-festival.webp`,
  observance:          `${GCS_BASE}/events/og/observance.webp`,
  seasonal:            `${GCS_BASE}/events/og/seasonal.webp`,
  school_term_start:   `${GCS_BASE}/events/og/school-term-start.webp`,
  school_term_end:     `${GCS_BASE}/events/og/school-term-end.webp`,
  exam_period:         `${GCS_BASE}/events/og/exam-period.webp`,
  enrollment_deadline: `${GCS_BASE}/events/og/enrollment-deadline.webp`,
  school_holiday:      `${GCS_BASE}/events/og/school-holiday.webp`,
  local_event:         `${GCS_BASE}/events/og/local-event.webp`,
};

// --- Hero images (in-page banners) ---

export const EVENT_TYPE_HEROES: Record<string, string> = {
  public_holiday:    `${GCS_BASE}/events/hero/public-holiday.webp`,
  national_day:      `${GCS_BASE}/events/hero/national-day.webp`,
  cultural_festival: `${GCS_BASE}/events/hero/cultural-festival.webp`,
  observance:        `${GCS_BASE}/events/hero/observance.webp`,
  seasonal:          `${GCS_BASE}/events/hero/seasonal.webp`,
  school_term_start: `${GCS_BASE}/events/hero/school-term-start.webp`,
};

// --- Category icons ---

export const NATURE_ICONS: Record<string, string> = {
  monsoon:      `${GCS_BASE}/icons/nature-monsoon.png`,
  'dry-season': `${GCS_BASE}/icons/nature-dry-season.png`,
  bloom:        `${GCS_BASE}/icons/nature-bloom.png`,
  aurora:       `${GCS_BASE}/icons/nature-aurora.png`,
  extreme:      `${GCS_BASE}/icons/nature-extreme.png`,
};

export const RELIGION_ICONS: Record<string, string> = {
  islamic:   `${GCS_BASE}/icons/religion-islamic.png`,
  hindu:     `${GCS_BASE}/icons/religion-hindu.png`,
  christian: `${GCS_BASE}/icons/religion-christian.png`,
  lunar:     `${GCS_BASE}/icons/religion-lunar.png`,
  buddhist:  `${GCS_BASE}/icons/religion-buddhist.png`,
};

export const ACADEMIC_CALENDAR_ICON = `${GCS_BASE}/icons/academic-calendar.png`;
export const LONG_WEEKEND_ICON = `${GCS_BASE}/icons/long-weekend.png`;

// --- Landing page images ---

export const LANDING_IMAGES = {
  heroAtlasBg:     `${GCS_BASE}/landing/hero-atlas-bg.webp`,
  exploreTrip:     `${GCS_BASE}/landing/explore-trip.png`,
  exploreAbroad:   `${GCS_BASE}/landing/explore-abroad.png`,
  exploreReligious: `${GCS_BASE}/landing/explore-religious.png`,
  exploreLeave:    `${GCS_BASE}/landing/explore-leave.png`,
  coverageMap:     `${GCS_BASE}/landing/coverage-map.webp`,
  ogBackground:    `${GCS_BASE}/landing/og-background.webp`,
};

// --- Homepage OG image ---

export const HOMEPAGE_OG_IMAGE = `${GCS_BASE}/landing/og-image.png`;

/** Pinterest-ready 2:3 vertical pin (1000x1500). Listed alongside the
 *  horizontal og:image so Pinterest's scraper picks the taller variant
 *  while Twitter/LinkedIn/WhatsApp keep the horizontal one. */
export const PINTEREST_PIN_IMAGE = `${GCS_BASE}/landing/pinterest-pin.png`;

// --- Month hub OG images ---

export const MONTH_OG_IMAGES: Record<number, string> = {
  1:  `${GCS_BASE}/months/og/january.webp`,
  2:  `${GCS_BASE}/months/og/february.webp`,
  3:  `${GCS_BASE}/months/og/march.webp`,
  4:  `${GCS_BASE}/months/og/april.webp`,
  5:  `${GCS_BASE}/months/og/may.webp`,
  6:  `${GCS_BASE}/months/og/june.webp`,
  7:  `${GCS_BASE}/months/og/july.webp`,
  8:  `${GCS_BASE}/months/og/august.webp`,
  9:  `${GCS_BASE}/months/og/september.webp`,
  10: `${GCS_BASE}/months/og/october.webp`,
  11: `${GCS_BASE}/months/og/november.webp`,
  12: `${GCS_BASE}/months/og/december.webp`,
};

// --- Hub OG image mapping ---

export const HUB_OG_IMAGES: Record<string, string> = {
  religion_islamic:   `${GCS_BASE}/hubs/og/religion-islamic.webp`,
  religion_christian: `${GCS_BASE}/hubs/og/religion-christian.webp`,
  religion_hindu:     `${GCS_BASE}/hubs/og/religion-hindu.webp`,
  religion_lunar:     `${GCS_BASE}/hubs/og/religion-lunar.webp`,
  religion_buddhist:  `${GCS_BASE}/hubs/og/religion-buddhist.webp`,
  nature:             `${GCS_BASE}/events/og/seasonal.webp`,
};

// --- Path builders (DB image_url → GCS fallback) ---

export function getCountryOgImage(country: { code: string }): string {
  return `${GCS_BASE}/countries/og/${country.code.toLowerCase()}.webp`;
}

export function getCountryHeroImage(country: { imageUrl?: string; code: string }): string {
  return country.imageUrl || `${GCS_BASE}/countries/hero/${country.code.toLowerCase()}.webp`;
}

/** University IDs with dedicated hero/OG webps in GCS. Others fall back to country hero. */
const UNIVERSITY_IMAGE_IDS = new Set([
  'ub', 'ugm', 'ui', 'undip', 'unej', 'unesa', 'unhas', 'unpad', 'unsri', 'usu',
]);

export function getUniversityOgImage(uni: { imageUrl?: string; id: string; country?: string }): string {
  if (uni.imageUrl) return uni.imageUrl;
  if (UNIVERSITY_IMAGE_IDS.has(uni.id)) return `${GCS_BASE}/universities/og/${uni.id}.webp`;
  if (uni.country) return getCountryOgImage({ code: uni.country });
  return DEFAULT_OG_IMAGE;
}

export function getUniversityHeroImage(uni: { imageUrl?: string; id: string; country?: string }): string {
  if (uni.imageUrl) return uni.imageUrl;
  if (UNIVERSITY_IMAGE_IDS.has(uni.id)) return `${GCS_BASE}/universities/hero/${uni.id}.webp`;
  if (uni.country) return getCountryHeroImage({ code: uni.country });
  return DEFAULT_OG_IMAGE;
}

/** City IDs with dedicated hero/OG webps in GCS. Others fall back to country hero. */
const CITY_IMAGE_IDS = new Set([
  'bali', 'bandung', 'jakarta', 'jember', 'kuala-lumpur', 'makassar', 'malang',
  'medan', 'palembang', 'semarang', 'singapore', 'surabaya', 'yogyakarta',
]);

export function getCityOgImage(city: { imageUrl?: string; id: string; country?: string }): string {
  if (city.imageUrl) return city.imageUrl;
  if (CITY_IMAGE_IDS.has(city.id)) return `${GCS_BASE}/cities/og/${city.id}.webp`;
  if (city.country) return getCountryOgImage({ code: city.country });
  return DEFAULT_OG_IMAGE;
}

export function getCityHeroImage(city: { imageUrl?: string; id: string; country?: string }): string {
  if (city.imageUrl) return city.imageUrl;
  if (CITY_IMAGE_IDS.has(city.id)) return `${GCS_BASE}/cities/hero/${city.id}.webp`;
  if (city.country) return getCountryHeroImage({ code: city.country });
  return DEFAULT_OG_IMAGE;
}

export function getCityLogoUrl(city: { logoUrl?: string; id: string }): string {
  return city.logoUrl || `${GCS_BASE}/cities/logos/${city.id}.png`;
}

export function getUniversityLogoUrl(uni: { logoUrl?: string; id: string }): string {
  return uni.logoUrl || `${GCS_BASE}/universities/logos/${uni.id}.png`;
}

export function getEventMapImage(eventGroup: string): string {
  return `${GCS_BASE}/maps/${eventGroup}.webp`;
}

/** Event OG: custom → type fallback → default */
export function getEventOgImage(event: { imageUrl?: string; type: string }): string {
  return event.imageUrl || EVENT_TYPE_OG_IMAGES[event.type] || DEFAULT_OG_IMAGE;
}

/** Event hero: custom → type fallback → undefined */
export function getEventHeroImage(event: { imageUrl?: string; type: string }): string | undefined {
  return event.imageUrl || EVENT_TYPE_HEROES[event.type];
}

// --- Twibbon logos ---

export const TWIBBON_LOGOS_BASE = `${GCS_BASE}/twibbon/logos`;

/** Evergreen OG: served from GCS bucket */
export function getEvergreenOgImage(pageType: string, slug: string): string {
  return `${GCS_BASE}/${pageType}/og/${slug}.webp`;
}

/**
 * Resolve OG image for evergreen pages with smart fallbacks.
 * Admission pages → hero image (known 200 on GCS).
 * Long-weekend / university-admission → country OG image.
 * Everything else → DEFAULT_OG_IMAGE.
 */
export function getEvergreenOgImageResolved(entry: {
  pageType: string;
  slug: string;
  data: Record<string, unknown>;
}): string {
  switch (entry.pageType) {
    case 'admission':
    case 'admission-calendar':
    case 'admission-faq': {
      const slug = entry.data.admissionSlug as string | undefined;
      if (slug && ADMISSION_OG_SLUGS.has(slug)) {
        return getAdmissionOgImage(slug);
      }
      const pathway = entry.data.pathway as string | undefined;
      if (pathway) return getAdmissionHeroImage(pathway, CALENDAR_YEAR);
      break;
    }
    case 'university-admission': {
      const code = entry.data.countryCode as string | undefined;
      if (code) return getCountryOgImage({ code });
      break;
    }
    case 'long-weekend':
    case 'phenomenon': {
      const code = entry.data.countryCode as string | undefined;
      if (code) return getCountryOgImage({ code });
      break;
    }
  }
  return DEFAULT_OG_IMAGE;
}

// --- Admission page images ---

/** Admission slugs with dedicated OG images on GCS */
const ADMISSION_OG_SLUGS = new Set([
  'snbp-2026',
  'lolos-snbp-2026',
  'tidak-lolos-snbp-2026',
  'jadwal-penerimaan-ptn-2026',
]);

/** Dedicated OG image for an admission page slug */
export function getAdmissionOgImage(admissionSlug: string): string {
  return `${GCS_BASE}/admission/og/${admissionSlug}.webp`;
}

/** Hero illustration for an admission pathway (e.g. "snbp", year 2026) */
export function getAdmissionHeroImage(pathway: string, year: number): string {
  return `${GCS_BASE}/admission/hero/${pathway.toLowerCase()}-${year}.jpg`;
}

/** CTA illustration for accepted/rejected outcome cards */
export function getAdmissionCtaImage(outcome: 'lolos' | 'tidak-lolos', pathway: string, year: number): string {
  return `${GCS_BASE}/admission/cta/${outcome}-${pathway.toLowerCase()}-${year}.jpg`;
}
